import { createHttp, rejectWithApiError } from '@/services/http/create-http';
import type { AuthSession, LoginRequest } from '@/types/auth';

/**
 * Auth endpoints use their own client: no bearer header and no refresh-on-401, so a failed
 * refresh can never trigger another refresh.
 *
 * Contract (bovid-api/src/modules/auth/controllers/admin/admin-auth.controller.ts). The
 * refresh token travels only in the httpOnly cookie the API sets (same origin, so the browser
 * sends it):
 *   POST /auth/login    { portal, identifier, password, rememberMe } → AuthSession + cookie
 *   POST /auth/refresh  { rememberMe }                               → AuthSession (cookie rotated)
 *   POST /auth/logout   {}                                           → 204, cookie cleared
 */
const authHttp = createHttp(15_000);
// Sends and receives the refresh cookie also when the API is on another subdomain.
authHttp.defaults.withCredentials = true;
rejectWithApiError(authHttp);

export const authApi = {
  async login(body: LoginRequest & { rememberMe: boolean }): Promise<AuthSession> {
    const { data } = await authHttp.post<AuthSession>('/auth/login', body);
    return data;
  },

  /** `refreshToken` only to move a token kept from before the cookie into it. */
  async refresh(body: { rememberMe: boolean; refreshToken?: string }): Promise<AuthSession> {
    const { data } = await authHttp.post<AuthSession>('/auth/refresh', body);
    return data;
  },

  async logout(): Promise<void> {
    await authHttp.post('/auth/logout', {});
  },
};
